import * as Sentry from '@sentry/react'

import { IWindowJsEnv } from 'src/interfaces/index'

const _window = window as unknown as IWindowJsEnv
// Use this function if you just want to report an error to Airbrake (see below if you have special needs).
// Parameters:
//   error: Should be an Error-object. Other values are either converted into errors or discarded (if null or undefined)
//   params: An object that contains additional information about the error (optional)
//   context: An object that contains additional information about the context the error occured in (optional)

export const reportError = (
  error: any,
  params?: object,
  context: object = { component: 'client' }
): void => {
  reportErrorWithPromise(error, params, context).then().catch((_err) => { /* nothing */ })
  logToDevConsole('%c Airbrake reported:', 'background: #c0392b; color: black', error, params, context)
}

// Use this function if you want filter out non-error values and only report actual Error-objects to Airbrake.
// For example: when catching the result of a API-Wrapper function.
// Return value: true, if the error has been reported, or false, if nothing was reported.

export const reportOnlyErrorObject = (
  error: any,
  params?: object,
  context: object = { component: 'client' }
): boolean => {
  if (!(error instanceof Error)) {
    // Activate the following line to show filtered errors in the console
    // console.log(error);
    return false
  }
  reportError(error, params, context)
  return true
}

// Use this function when you want a guarantee that airbrake finished reporting an error
// For example: before you unload a page.
// Return value: Airbrake-object, if the error has been reported, or false, if nothing was reported.

export const reportErrorWithPromise = (
  error: any, // This can actually be anything
  params?: object,
  context: object = { component: 'client' }
): Promise<any | false> => {
  // Don't report errors that have been reported already
  // Don't report null or undefined errors (might come from the global error handler, see #3988)
  if (error === null || error === undefined || error.alreadyReported) {
    return Promise.reject(false)
  }
  // Ensure that we always report a real Error object to get a stack-trace
  if (!(error instanceof Error)) {
    if (typeof error === 'string') {
      // Strings are converted into an error-message
      error = new Error(error)
    } else {
      // Other non-error errors are converted into an unspecified error
      params = { ...params, value: error }
      error = new Error('unspecified error')
    }
  }
  // Mark the error as already reported and report it
  error.alreadyReported = true

  Sentry.withScope((scope) => {
    if (params) {
      scope.setContext('params', params)
    }
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, String(value))
      })
    }

    Sentry.captureException(error)
  })

  return Promise.resolve(true)
}

// Use this function, if you need to inform the developers about something.

export const logToDevConsole = (...args: any): void => {
  const w = (window as unknown as IWindowJsEnv)
  if (w.js_env && w.js_env.env === 'development') {
    // eslint-disable-next-line no-console
    console.log(...args)
  }
}

// Our central error reporting function does some extra stuff, before it reports an error to Airbrake.
// Therefore we pass any error that reaches the global handler to reportError().
window.addEventListener('error', (evt) => {
  try {
    reportError(evt.error)
  } catch (exception) {
    // If anything goes wrong in the global error handler we try to notify Sentry directly as a last resort.
    Sentry.withScope((scope) => {
      scope.setContext('params', { error: evt.error, exception })
      scope.setTag('component', 'client')
      Sentry.captureException(new Error('Exception in global error handler'))
    })
  }
}, false)

// The outdated-browser notice runs from src/index.html, before this bundle is parsed, and
// leaves its result on window.$buoop. Sentry events are not filtered by it: a browser below
// the thresholds cannot parse application.js, so Sentry never runs there in the first place.
//
// This checks that the script ran at all - a wrong asset path would otherwise remove the
// notice for everyone, silently. window.$buoop is set by src/index.html only, so its absence
// means "no index.html" (Jest, Storybook) rather than a failure.
//
// setTimeout, because src/index.tsx reaches this module through an earlier import than the
// one that runs Sentry.init. Without a client, captureException drops the event; measured.
if (_window.$buoop) {
  setTimeout(() => {
    if (typeof _window.$buoop?.notified !== 'boolean') {
      reportError('browser-update script did not run')
    }
  }, 0)
}
